import React, { useCallback, useEffect, useRef } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";
import type { PracticeNoteEvent } from "../../lib/practiceNoteModel";
import type { NoteName } from "../../lib/guitarEngine";
import { PRACTICE_STAFF_CHORD_SCALE } from "../../lib/practiceStaffLayout";
import {
  PRACTICE_STAFF_COLORS_ATMOSPHERIC,
  PRACTICE_STAFF_CONTAINER_STYLE,
  notationNoteStyle,
  paintStaveLines,
  serenityStaffDropShadow,
} from "../../lib/practiceStaffTheme";

const SCALE = PRACTICE_STAFF_CHORD_SCALE;

function buildVexKey(note: NoteName, octave: number): string {
  const baseName = note.replace("#", "").replace("b", "").toLowerCase();
  return `${baseName}/${octave}`;
}

function applyChordStaffHalos(container: HTMLDivElement, degrees: string[]) {
  const svg = container.querySelector("svg");
  if (!svg) return;
  const noteGroups = Array.from(svg.querySelectorAll(".vf-stavenote"));
  noteGroups.forEach((group, i) => {
    const degree = degrees[i] ?? "R";
    const el = group as SVGGElement;
    el.style.transition = "filter 300ms ease-out";
    el.style.filter = serenityStaffDropShadow(degree, true);
  });
}

export interface ChordAtmosphericStaffProps {
  events: PracticeNoteEvent[];
  className?: string;
}

export const ChordAtmosphericStaff: React.FC<ChordAtmosphericStaffProps> = ({
  events,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const renderScore = useCallback(() => {
    const host = containerRef.current;
    if (!host || events.length === 0) return;
    host.innerHTML = "";

    const palette = PRACTICE_STAFF_COLORS_ATMOSPHERIC;
    const width = host.clientWidth || 640;
    const height = host.clientHeight || 128;

    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();
    ctx.scale(SCALE, SCALE);

    const pad = 32;
    const staveW = (width - pad * 2) / SCALE;
    const staveY = Math.max(12, (height / SCALE - 56) / 2);
    const stave = new Stave(pad / SCALE, staveY, staveW);
    stave.addClef("treble");
    paintStaveLines(ctx, palette);
    stave.setContext(ctx).draw();

    const keys = events.map((ev) => {
      const octave = Math.floor(ev.midi / 12) - 1;
      const step = ev.step ?? ("C" as NoteName);
      return buildVexKey(step, octave);
    });

    const chordNote = new StaveNote({
      keys,
      duration: "w",
      autoStem: false,
    });

    events.forEach((ev, i) => {
      const step = ev.step ?? ("C" as NoteName);
      if (step.includes("#")) chordNote.addModifier(new Accidental("#"), i);
      else if (step.includes("b") && step.length > 1) chordNote.addModifier(new Accidental("b"), i);
    });

    chordNote.setStyle(
      notationNoteStyle({ atmospheric: true, active: true, degree: "R" }, palette)
    );

    const voice = new Voice({ numBeats: 4, beatValue: 4 });
    voice.addTickables([chordNote]);
    new Formatter().joinVoices([voice]).format([voice], staveW - 48);
    chordNote.setStave(stave);
    voice.draw(ctx, stave);

    applyChordStaffHalos(host, events.map((e) => e.degree ?? "R"));

    const svg = host.querySelector("svg");
    if (svg) {
      svg.setAttribute("width", String(width));
      svg.setAttribute("height", String(height));
      svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
      svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
      svg.style.width = "100%";
      svg.style.height = "100%";
      svg.style.display = "block";
      svg.style.overflow = "visible";
    }
  }, [events]);

  useEffect(() => {
    renderScore();
  }, [renderScore]);

  useEffect(() => {
    const host = containerRef.current;
    if (!host) return;
    const ro = new ResizeObserver(() => renderScore());
    ro.observe(host);
    return () => ro.disconnect();
  }, [renderScore]);

  return (
    <div
      ref={containerRef}
      className={`w-full h-full min-h-0 ${className}`}
      style={PRACTICE_STAFF_CONTAINER_STYLE}
    />
  );
};
